import db from '@klinx/utils/localstorage';
import { LANGUAGE_MAP } from '@klinx/utils/lang-map';
import { ccsDeviceApi } from '@klinx/ccs/src/api/index';
import { baseMessageApi } from '@klinx/base/src/api/index.js';
import { ROUTE_APPCODE_MAP } from '@klinx/base/src/config/route-app-map.js';
import SockJS from 'sockjs-client';
import Stomp from 'stompjs';
import i18n from '@/lang';
const $t = (args) => i18n.t(args);
const MAX_WEBSOCKET_RETRY_TIMES = 3;

export default {
  data() {
    return {
      stompClient: null,
      notifyTimer: null,
      speakToken: null,
      speaker: null,
      speakerIns: null,
      speakMsgList: [],
      speakMsgId: '',
      hasInitedSpeak: false,
      speechMsgResend: false,
      msgNotifyList: [],
      wsRetryCount: 0, // websocket重连次数记录，最多3次
      curSpeakCount: 0, // 当前播报消息的播放次数（剩余）
      hasStopSpeaker: false // 是否中断了播报（正常中断）
    };
  },
  beforeDestroy() {
    if (this.stompClient) {
      this.stompClient.disconnect ? this.stompClient.disconnect() : this.stompClient.close();
    }
    if (this.speakerIns) {
      this.stopSpeaker();
      db.remove('isSpeaking');
    }
    this.$eventBus.$off('testSpeaker');
  },
  computed: {
    userId() {
      return this.$store.state.account.user?.id || '';
    }
  },
  methods: {
    /**
     * webSocket建立链接
     */
    connect(_self) {
      const vm = this;
      const userToken = db.get('TOKEN', '');
      this.socket = new SockJS(`/api/msg/ws?token=Bearer ${userToken}`);
      this.stompClient = Stomp.over(this.socket);

      // 对象存入vuex
      this.$store.commit('account/websocket', this.stompClient);
      this.stompClient.connect({},
        frame => {
          this.wsRetryCount = 0;

          try {
            if (window.speechSynthesis) {
              this.speaker = window.speechSynthesis;
              this.speakerIns = new SpeechSynthesisUtterance();
              this.speakerIns.onend = this.onSpeakEnd;
              this.speakerIns.onerror = this.onSpeakError;
              this.$eventBus.$off('testSpeaker');
              this.$eventBus.$on('testSpeaker', this.handleMsgsSpeak);
              this.hasInitedSpeak = true;
            }
          } finally {
            // 上报语音实例化结果
            setTimeout(() => {
              const voice = this.hasInitedSpeak ? this.speaker.getVoices().filter(v => v.localService && v.lang === 'zh-CN')[0] : {};
              baseMessageApi.sendSpeechMsgRes({
                msgId: '',
                userId: this.userId,
                success: this.hasInitedSpeak,
                initState: this.hasInitedSpeak ? `${$t('语音实例初始化成功：')}${voice?.voiceURI}` : $t('语音实例初始化失败')
              });
            }, 1000);
          }
          const lang = LANGUAGE_MAP[i18n.locale].replace('_', '-'); // saas后端语言值用横杠
          this.stompClient.subscribe('/user/queue/msg/innerMsg', tick => {
            let msgData = JSON.parse(tick.body);
            let hasMatchLang = true;

            // 消息模板支持多语言，如果是多语言下，返回数组，根据当前语言或默认启用开关取对应的消息模板
            if (msgData instanceof Array) {
              hasMatchLang = msgData.some(item => item.lang === lang || !!item.isDefault); // 如对应的语言模板或者有默认启用的消息
              msgData = msgData.find(item => item.lang === lang) || msgData.find(item => !!item.isDefault) || msgData[0]; // 先取匹配语言，然后默认启用的，最后默认第一个
            }

            this.handleMsg(_self, vm, {
              platform: 'saas',
              ...msgData,
              needSpeaker: msgData.msgType.code === 'NOTIFY_POPUP_SPEECH' && hasMatchLang, // 语音消息需要有匹配模板才播报
              msgType: msgData.msgType.code === 'PUBLICITY' ? 'notify' : 'popup', // PUBLICITY\NOTIFY_POPUP
              speechTimes: msgData.playCount
            });
          });
        },
        error => {
          console.log(error);
          if (this.wsRetryCount < MAX_WEBSOCKET_RETRY_TIMES) {
            this.wsRetryCount += 1;
            this.connect();
            baseMessageApi.sendSpeechMsgRes({
              msgId: '',
              userId: this.userId,
              success: false,
              initState: `${$t('websocket连接出错：')}${error}。${$t('重连次数：')}${this.wsRetryCount}/${MAX_WEBSOCKET_RETRY_TIMES}`
            });
          } else {
            this.$confirm($t('当前消息推送相关功能连接失败，是否刷新页面重连'), $t('温馨提示'), {
              confirmButtonText: $t('刷新')
            }).then(() => {
              window.location.reload();
            }, () => {});
          }
        }
      );
    },
    /**
     * paas websocket连接
     */
    connectPaas(_self) {
      if (typeof WebSocket === 'undefined') {
        return;
      }

      if (window.speechSynthesis) {
        this.speaker = window.speechSynthesis;
        this.speakerIns = new SpeechSynthesisUtterance();
        this.speakerIns.onend = this.onSpeakEnd;
        this.speakerIns.onerror = this.onSpeakError;
        this.$eventBus.$off('testSpeaker');
        this.$eventBus.$on('testSpeaker', this.handleMsgsSpeak);
      }

      const vm = this;
      const userToken = db.get('TOKEN', '');
      const appId = db.get('APPID', '');
      const tId = db.get('TID', '');
      const uId = db.get('UID', '');
      const defaultWsPrefix = `${window.location.protocol.includes('https') ? 'wss:' : 'ws:'}//${window.location.hostname}:30086`;
      const wsUrl = `${process.env.VUE_APP_PAAS_WEBSOCKET_DOMAIN_PREFIX || defaultWsPrefix}/notice`;

      this.stompClient = new WebSocket(wsUrl);
      this.$store.commit('account/websocket', this.stompClient);

      function Heartbeat(beatFunc) {
        this.timer = null;
        this.interval = 60 * 1000; // 1分钟一次心跳消息，服务端10分钟检查一次
        this.start = () => {
          this.timer && clearInterval(this.timer);
          this.timer = setInterval(beatFunc, this.interval);
        };
        this.stop = () => {
          this.timer && clearInterval(this.timer) && (this.timer = null);
        };
      }

      this.stompClient.sendHeartbeat = function() {
        this.send(JSON.stringify({
          msgId: new Date().getTime().toString(),
          cmd: 'heartbeat',
          data: {
            uid: uId
          }
        }));
      };

      // 初始发生登录信息
      this.stompClient.onopen = function() {
        this.send(JSON.stringify({
          seq: new Date().getTime(),
          cmd: 'login',
          data: {
            token: userToken,
            appId: appId,
            tid: tId,
            uid: uId
          }
        }));
        this.heartbeat = new Heartbeat(this.sendHeartbeat.bind(this));
        this.heartbeat.start();
      };

      // 接收处理信息
      this.stompClient.onmessage = (e) => {
        const msgData = JSON.parse(e.data);

        if (msgData?.cmd === 'login') {
          console.log(">>> CONNECT PAAS");
        } else if (msgData?.cmd !== 'heartbeat') {
          console.log("<<< REVIEVE\n", msgData);
        }

        if (msgData?.cmd === 'pushInnerNoticeJob' || msgData?.cmd === 'pushBannerNoticeJob') {
          const lang = LANGUAGE_MAP[i18n.locale]; // saas后端语言值用横杠
          let hasMatchLang = true;
          let langData = {};

          // 消息模板支持多语言，如果是多语言下，返回数组，根据当前语言或默认启用开关取对应的消息模板
          if (msgData.data.many_lang_list?.length) {
            hasMatchLang = msgData.data.many_lang_list.some(item => item.language === lang || !!item.isDefault); // 如对应的语言模板或者有默认启用的消息
            langData = msgData.data.many_lang_list.find(item => item.language === lang) || msgData.data.many_lang_list.find(item => !!item.isDefault) || msgData.data.many_lang_list[0]; // 先取匹配语言，然后默认启用的，最后默认第一个
          }

          this.handleMsg(_self, vm, {
            platform: 'paas',
            ...msgData.data,
            ...langData,
            lang: (langData.language || '').replace('_', '-'), // paas后端语言值是下划线，需要转换成横杠给播报api用
            id: msgData.msgId, // 如果多语言，每个子消息里的id不一样，取最外层的msgId做唯一标识
            bannerEndTime: msgData.data.push_end_time,
            needSpeaker: !!msgData.data.voice_announcements && hasMatchLang, // paas侧的语音开关
            msgType: msgData.cmd === 'pushBannerNoticeJob' ? 'notify' : 'popup',
            // todo: 根据paas返回的调整字段引入
            handlerUrl: msgData.data.inner_url,
            handlerParams: msgData.data.msg_handler_params,
            speechTimes: msgData.data.announcement_num
          });
        }
      };

      // 关闭回调
      this.stompClient.onclose = function(e) {
        console.log(">>> DISCONNECT PAAS", e);
        this.heartbeat && this.heartbeat.stop();
      };

      // 出错回调
      this.stompClient.onerror = function(e) {
        console.log(">>> CONNECT PAAS EORROR", e);
        this.heartbeat && this.heartbeat.stop();
      };
    },
    handleMsg(_self, vm, wsData) {
      const { platform, id, content, title, msgType, type, bannerEndTime, needSpeaker, reSend, speechTimes, confirmId, lang } = wsData; // type是saas才有，消息状态开关的
      const closable = wsData.enable_close || wsData.closable; // paas: enable_close, saas: closable

      // 通知更新导航栏消息列表
      this.$eventBus.$emit('initNotifyMsgs');

      if (msgType === 'notify') {
        /**
          * 每次收到通知栏推送
          *  1. 把消息ID存起来到localStorage
          *  2. 拿最新消息ID对比localStorage中的id
          *      若相等，则不显示通知栏
          *      若不相等，则显示通知栏，并更新localStorage中的id
          */
        const localNotify = db.get('NOTIFY');
        // 关于type，saas独立时，消息到期会推一个type=CLOSE关闭，paas不会推，虽然另外处理时间关闭
        const notifyParam = { id, content, closable, type, visible: type === undefined || type === 'SHOWOUT' };
        const exprieTime = (bannerEndTime && this.dayjs(bannerEndTime).diff(this.dayjs(), 'second')) || '';
        this.notifyTimer && clearTimeout(this.notifyTimer);

        if (exprieTime !== '' && exprieTime < 20) {
          this.notifyTimer = setTimeout(() => {
            this.$store.commit('account/setNotify', { ...notifyParam, visible: false });
          }, exprieTime * 1000);
        }

        // 没有更新不触发重新轮播
        if (localNotify?.id === id &&
          localNotify?.content === content &&
          localNotify?.closable === closable &&
          localNotify?.type === type) {
          return;
        }

        /**
          * 允许更新通知栏的情况：
          * 当之前消息的状态改变时，后端会推送一条，id相同且type不同（saas才有）
          * 有另外一条通知栏推送时，本地id和最新id不一致
          */
        if ((localNotify?.id === id && localNotify?.type !== type) || localNotify?.id !== id) {
          this.$store.commit('account/setNotify', notifyParam);
        }
      } else if (msgType === 'popup') {
        const { handlerUrl = '', handlerParams = '', sourceModuleCode, app_id } = wsData; // saas:sourceModuleCode, paas: app_id
        const appList = db.get('APPLIST', []);
        const currentApp = db.get('CURRENTAPP', {});
        let popType = 0;
        handlerUrl.includes('{DOWNLOND}') ? popType = 1 : '';
        handlerUrl.includes('{DIALOG}') ? popType = 2 : '';
        const isDevice = handlerUrl.includes('/monitoring/dyeing-control/monitor-details/index') ||
          handlerUrl.includes('/ccs/monitor/monitoring/step/index');
        const isEMS = sourceModuleCode === 'EMS' || (appList.some(app => app.appId === app_id && app.code === 'iEMS'));
        const keepNotify = isDevice || isEMS || needSpeaker; // 设备、EMS、语音播报的消息不自动关闭

        const cases = {
          0: _ => {
            if (this.msgNotifyList.includes(id)) {
              return;
            }

            const h = this.$createElement;
            const msgInstance = this.$notify({
              title: title,
              iconClass: 'el-icon-warning',
              customClass: 'primaryClass',
              dangerouslyUseHTMLString: true,
              message: h(
                'div', {
                  class: 'unlockNotify'
                },
                [
                  h(
                    'p', {
                      class: 'txt'
                    },
                    content
                  ),
                  h('br'),
                  h(
                    'div', {
                      class: 'btn'
                    },
                    [
                      h(
                        'el-button', {
                          class: 'ignore-btn',
                          on: {
                            click: function() {
                              msgInstance.close();
                            }
                          }
                        },
                        $t('忽略')
                      ),
                      h(
                        'el-button', {
                          class: 'check-btn',
                          on: {
                            click: async function() {
                              msgInstance.close();
                              if (isDevice) {
                                const param = handlerParams ? JSON.parse(handlerParams) : {};
                                const handleFunc = () => {
                                  ccsDeviceApi.findExInfo(param.deviceId).then(res => {
                                    if (res.data.isSuccess) {
                                      const {
                                        data
                                      } = res.data;
                                      const item = {
                                        runStatus: data.runStatus,
                                        dcDeviceId: param.dcDeviceId,
                                        devName: data.name
                                      };
                                      _self.$store.dispatch('monitorDetails/markdownClickedDev',
                                        item);
                                    }
                                  });
                                  const timeMark = new Date().getTime();
                                  localStorage.setItem(`${timeMark}-devId`, param.deviceId);
                                  localStorage.setItem('dcDeviceId', param.dcDeviceId);
                                  localStorage.setItem('devId', param.deviceId);
                                  localStorage.setItem('traceability', '0');
                                  localStorage.setItem('devDetailTag', 'fullControl');
                                  _self.$store.dispatch('monitorDetails/markdownTag', 'fullControl');
                                  _self.$router.push({
                                    path: handlerUrl,
                                    query: {
                                      deviceId: param.deviceId,
                                      time: timeMark
                                    }
                                  });
                                };

                                platform === 'saas' ? _self.$eventBus.$emit('updateAppRoute', 'iCCS', handleFunc) : handleFunc();
                              } else if (isEMS) { // saas和paas分别的判断
                                var rowData = {};
                                if (handlerUrl.length >= 19) {
                                  rowData.id = handlerUrl.substring(handlerUrl.length - 19);
                                  rowData.name = content;
                                }
                                if (rowData.id) {
                                  const handleFunc = () => {
                                    sessionStorage.setItem('emsReportDetail', JSON.stringify(rowData));
                                    _self.$router.push({
                                      path: '/ems/analyze/energy-report/index',
                                      query: {
                                        type: 'detail'
                                      }
                                    });
                                  };
                                  platform === 'saas' ? _self.$eventBus.$emit('updateAppRoute', 'iEMS', handleFunc) : handleFunc();
                                }
                              } else if (handlerUrl) {
                                const app = (handlerUrl.match(/^\/(\w+)\//) || [])[0]?.replace(/\//g, '');
                                const handleFunc = () => {
                                  const param = handlerParams ? JSON.parse(handlerParams) : {};
                                  _self.$router.push({
                                    path: handlerUrl,
                                    query: {
                                      id: param.id || ''
                                    }
                                  });
                                };

                                (!currentApp.code || currentApp.code === 'home') && ROUTE_APPCODE_MAP[app]
                                  ? _self.$eventBus.$emit('updateAppRoute', ROUTE_APPCODE_MAP[app], handleFunc) : handleFunc();
                              } else {
                                _self.$router.push({
                                  path: '/base-config/message/center'
                                });
                              }
                            }
                          }
                        },
                        $t('查看详情')
                      )
                    ]
                  )
                ]
              ),
              offset: 50,
              duration: keepNotify ? 0 : 8000,
              onClose: () => {
                this.msgNotifyList = this.msgNotifyList.filter(item => item !== id);
                this.stopSpeaker('', id);
              }
            });
            this.msgNotifyList.push(id);
          },
          1: _ => {
            const h = this.$createElement;
            const newHandlerUrl = handlerUrl.substring(10);
            const iconClass = !newHandlerUrl.includes($t('链接失效')) ? 'el-icon-success' : 'el-icon-warning';
            const linkClass = newHandlerUrl.includes($t('链接失效')) ? 'hidden-link' : 'show-link';
            const msgLog = this.$notify({
              title: title,
              iconClass,
              customClass: 'primaryClass',
              dangerouslyUseHTMLString: true,
              message: h(
                'div', {
                  class: 'unlockNotify'
                },
                [
                  h(
                    'p', {
                      class: 'txt'
                    },
                    content
                  ),
                  h('br'),
                  h(
                    'div', {
                      class: 'btn'
                    },
                    [
                      h(
                        'el-link', {
                          class: linkClass,
                          on: {
                            click: function() {
                              const link = document.createElement('a');
                              link.href = newHandlerUrl;
                              link.download = $t('下载');
                              link.click();
                              window.URL.revokeObjectURL(link.href);
                              msgLog.close();
                            }
                          }
                        },
                        $t('下载')
                      )
                    ]
                  )
                ]
              ),
              offset: 50,
              duration: 0
            });
          },
          2: _ => { // 料单弹框
            const h = this.$createElement;
            const newHandlerUrl = handlerUrl.substring(8);
            const paramsResult = this.analysisParams(newHandlerUrl);
            const dialogType = paramsResult?.dialogType || 0; // 0： 料单弹框

            const dialogPop = this.$notify({
              title: title,
              iconClass: 'el-icon-warning',
              customClass: 'primaryClass',
              dangerouslyUseHTMLString: true,
              onClose: () => this.stopSpeaker('', id),
              message: h(
                'div', {
                  class: 'unlockNotify'
                },
                [
                  h(
                    'p', {
                      class: 'txt'
                    },
                    content
                  ),
                  h('br'),
                  h(
                    'div', {
                      class: 'btn'
                    },
                    [
                      h(
                        'el-button', {
                          class: 'ignore-btn',
                          on: {
                            click: function() {
                              dialogPop.close();
                            }
                          }
                        },
                        $t('忽略')
                      ),
                      h(
                        'el-button', {
                          class: 'check-btn',
                          on: {
                            click: function() {
                              const caseDialogs = {
                                0: _ => {
                                  vm.materialDrawer = {
                                    ...vm.materialDrawer,
                                    title: vm.$t('料单详情'),
                                    visible: true,
                                    params: {
                                      stuffCode: paramsResult.stuffCodes || paramsResult.stuffCode || '',
                                      deviceType: paramsResult.deviceType || ''
                                    }
                                  };
                                }
                              };
                              caseDialogs[dialogType] && caseDialogs[dialogType]();
                              dialogPop.close();
                            }
                          }
                        },
                        $t('查看')
                      )
                    ]
                  )
                ]
              ),
              offset: 50,
              duration: 0
            });
          }
        };
        cases[popType] && cases[popType]();

        // 如果需要语音播报则插入队列处理消息，只播报标题
        if (needSpeaker) {
          this.speechMsgResend = !!reSend;
          this.handleMsgsSpeak(title, id, speechTimes, lang || 'zh-CN', confirmId || id);
        }
      }
    },
    /**
     * 解析参数
     */
    analysisParams(paramsString) {
      paramsString = paramsString ? String(paramsString) : '';
      const paramsStringArrs = paramsString.split('&');
      let result = {};
      if (paramsStringArrs && paramsStringArrs.length > 0) {
        paramsStringArrs.map(item => {
          if (item.indexOf('=') > -1) {
            const key = item.split('=')[0].trim();
            const value = item.split('=')[1].trim();
            result = {
              ...result,
              [key]: value
            };
          } else {
            result = {
              ...result,
              other: item
            };
          }
          return item;
        });
      }
      return result;
    },
    handleMsgsSpeak(msg, id, speechTimes, lang, confirmId) {
      const isSpeaking = db.get('isSpeaking', false);
      // 有播报，但当前页面非播报中的页面
      if (isSpeaking && !this.speakToken) {
        return;
      }

      if (!this.speakerIns.voice || this.speakerIns.voice.lang !== lang) {
        const voices = this.speaker.getVoices();
        console.log('Supported voices: ', voices);
        const voice = voices.filter(v => v.localService && v.lang === lang)[0] || voices.filter(v => v.lang === lang)[0]; // 优先用本地的，不支持的话需要电脑里添加语音包；尝试连远程兜底
        console.log('Use voice: ', voice);
        voice && (this.speakerIns.voice = voice);
      }

      // 记录当前播报token
      !this.speakToken && (this.speakToken = this.dayjs().valueOf());
      !isSpeaking && db.save('isSpeaking', true);

      // 插入播报消息
      this.speakMsgList.push({ msg, id, speechTimes });
      this.$store.commit('account/setSpeakMsgList', this.speakMsgList);
      this.curSpeakCount = speechTimes || 1; // 记录播报次数
      this.speakMsgList.length === 1 && this.startSpeak(msg, lang, confirmId);
    },
    setSpeakerIns(text, lang) {
      this.speakerIns.text = text;
      this.speakerIns.lang = lang;
      this.speakerIns.volume = '1';
      this.speakerIns.rate = 1;
      this.speakerIns.pitch = 1;
    },
    startSpeak(msg, lang, confirmId) {
      this.speakMsgId = db.get('platform') !== 'paas' ? confirmId : ''; // 记录当前播报消息的id，用于上报结果（仅saas可以上报，所以只记录saas的id）
      this.setSpeakerIns(msg, lang);
      this.speaker.cancel(); // 每次播放前先取消重置
      this.speaker.speak(this.speakerIns);
    },
    stopSpeaker(e = '', msgId) {
      const isSpeaking = db.get('isSpeaking', false);
      const { id: curMsgId } = this.speakMsgList[0] || {}; // 当前播放消息
      this.hasStopSpeaker = true;

      // 直接终止，或终止指定（当前）播报
      if (!msgId || msgId === curMsgId) {
        this.setSpeakerIns(e);
        this.speaker.cancel(this.speakerIns);
      }

      // 播放中的手动关闭播报，上报通知并检查未播报消息
      if (msgId === curMsgId && isSpeaking) {
        // 上报播放取消结果
        this.speakMsgId && baseMessageApi.sendSpeechMsgRes({
          msgId: this.speakMsgId,
          userId: this.userId,
          success: true,
          log: $t('手动关闭消息取消播放')
        });

        this.handleShiftMsg();
      } else if (msgId !== curMsgId && this.speakMsgList.some(item => item.id === msgId)) { // 剔除指定的未播报的消息
        this.speakMsgList = this.speakMsgList.filter(item => item.id !== msgId);
        this.$store.commit('account/setSpeakMsgList', this.speakMsgList);
      }
    },
    handleShiftMsg() {
      // 播放次数完成后，剔除消息
      this.speakMsgList.shift();
      this.$store.commit('account/setSpeakMsgList', this.speakMsgList);

      if (!this.speakMsgList.length) {
        db.remove('isSpeaking');
        this.speakToken = null;
        return;
      }

      const { msg, id, speechTimes } = this.speakMsgList[0];
      this.curSpeakCount = speechTimes || 1;
      this.startSpeak(msg, id);
    },
    onSpeakEnd() {
      // 上报播放成功结果
      this.speakMsgId && baseMessageApi.sendSpeechMsgRes({
        msgId: this.speakMsgId,
        userId: this.userId,
        success: true
      });

      // 多次播放时，重复播放消息
      if (this.curSpeakCount > 1) {
        this.curSpeakCount -= 1;
        this.startSpeak(this.speakMsgList[0].msg, this.speakMsgList[0].id);
        return;
      }

      this.handleShiftMsg();
    },
    onSpeakError(event) {
      db.remove('isSpeaking'); // 播报出错也要取消占位

      if (this.hasStopSpeaker) {
        this.hasStopSpeaker = false;
        return;
      }

      console.log(`${$t('语音消息播报出错：')}${event.error}`);
      !!localStorage.getItem('testSpeaker') && this.$message($t('语音消息播报出错，请刷新页面重试')); // 本地测试

      // 播放失败也清理消息
      this.speakMsgList.shift();
      this.$store.commit('account/setSpeakMsgList', this.speakMsgList);

      // 上报播放失败结果
      this.speakMsgId && baseMessageApi.sendSpeechMsgRes({
        msgId: this.speakMsgId,
        userId: this.userId,
        success: false,
        log: this.speechMsgResend ? `${$t('重发消息播放失败：')}${event.error}` : `${$t('新消息播放失败：')}${event.error}`
      });
    }
  }
};
